/**
 * 加密工具函数
 * 对标 tv-linux 项目 src/util/encrypt.js
 */

import CryptoJS from 'crypto-js';
import { getCookieAmend } from './apiUtils';

const { Base64 } = CryptoJS.enc;

/**
 * 16进制字符串转 Uint8Array
 */
function hexToArrayBuffer(hex: string): Uint8Array {
  if (!hex) return new Uint8Array([]);
  const matches = hex.match(/[\da-f]{2}/gi);
  if (!matches) return new Uint8Array([]);
  const buffer = new Uint8Array(matches.map((h) => parseInt(h, 16)));
  return buffer;
}

/**
 * ArrayBuffer 转 16进制字符串
 */
function arrayBufferToHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((x) => `00${x.toString(16)}`.slice(-2))
    .join('');
}

/**
 * 导入 AES-GCM 密钥
 */
async function importKey(array: Uint8Array): Promise<CryptoKey> {
  return window.crypto.subtle.importKey(
    'raw',
    array as BufferSource,
    {
      name: 'AES-GCM',
    },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * AES-GCM 加密
 */
async function encryptMessage(key: string | Uint8Array, message: string | Uint8Array): Promise<Uint8Array> {
  let keyArray: Uint8Array;
  let exKey: CryptoKey;

  if (typeof key === 'string') {
    keyArray = hexToArrayBuffer(key);
    exKey = await importKey(keyArray);
  } else {
    keyArray = key;
    exKey = await importKey(keyArray);
  }

  let encoded: Uint8Array;
  if (typeof message === 'string') {
    const enc = new TextEncoder();
    encoded = enc.encode(message);
  } else {
    encoded = message;
  }

  // 后台要求的 iv 算法：从密钥中随机取12字节
  const randStartIdx = Math.floor(Math.random() * (keyArray.length - 12));
  const iv = keyArray.slice(randStartIdx, randStartIdx + 12);

  // 加密
  const encrypted = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
      tagLength: 128,
    },
    exKey,
    encoded as BufferSource
  );

  // 将 iv 和密文拼接
  const encryptedArray = new Uint8Array(encrypted);
  const result = new Uint8Array(iv.length + encryptedArray.length);
  result.set(iv);
  result.set(encryptedArray, iv.length);
  return result;
}

/**
 * AES-GCM 加密（带可选密钥生成）
 */
async function aESGCM(message: string, key?: string): Promise<Uint8Array> {
  if (key) {
    return encryptMessage(key, message);
  }
  const skey = await window.crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256,
    },
    true,
    ['encrypt', 'decrypt']
  );
  return encryptMessage(skey as unknown as Uint8Array, message);
}

/**
 * 生成 ECDH 协商密钥
 * 对标 tv-linux 的 createEcdh 函数
 */
async function createEcdh(): Promise<{ clientPublicKeyHex: string; sharedSecretHex: string } | null> {
  try {
    // 浏览器不支持情况下，启动降级方案
    if (!window.crypto?.subtle) {
      return null;
    }

    // 16进制转数组
    const hex2Arr = (str: string): Uint8Array => {
      if (!str) {
        return new Uint8Array();
      }
      const arr: number[] = [];
      for (let i = 0, len = str.length; i < len; i += 2) {
        arr.push(parseInt(str.substr(i, 2), 16));
      }
      return new Uint8Array(arr);
    };

    // 数组转16进制
    const buf2Hex = (buf: ArrayBuffer): string =>
      Array.from(new Uint8Array(buf))
        .map((x) => `00${x.toString(16)}`.slice(-2))
        .join('');

    // 服务器公钥（固定值）
    const serPublicKeyHex =
      '0484705184c0cdfdb1ace0268bcae87f5320913222d3c71ca777defe3999e88c2c8ed1c5fed78287b90c7d8b01b81c9b7db20bb669e4899418c2038637d8bb4f13';
    const serPublicKey = hex2Arr(serPublicKeyHex);

    // 生成客户端密钥对
    const bobKey = await window.crypto.subtle.generateKey(
      {
        name: 'ECDH',
        namedCurve: 'P-256',
      },
      false,
      ['deriveKey', 'deriveBits']
    );

    // 导出客户端公钥
    const clientPublicKeyExported = await window.crypto.subtle.exportKey('raw', bobKey.publicKey);
    const clientPublicKeyHex = buf2Hex(clientPublicKeyExported);

    // 导入服务器公钥
    const aliceKeyImported = await window.crypto.subtle.importKey(
      'raw',
      serPublicKey as BufferSource,
      {
        name: 'ECDH',
        namedCurve: 'P-256',
      },
      true,
      []
    );

    // 生成共享密钥
    const sharedSecret = await window.crypto.subtle.deriveBits(
      {
        name: 'ECDH',
        public: aliceKeyImported,
      },
      bobKey.privateKey,
      256
    );
    const sharedSecretHex = buf2Hex(sharedSecret);

    return {
      clientPublicKeyHex,
      sharedSecretHex,
    };
  } catch (e) {
    console.error('createEcdh error:', e);
    return null;
  }
}

/**
 * 登录加密
 * 对标 tv-linux 的 getEncryptedFormDataLogin 函数
 *
 * @param option 登录参数
 * - account: 账号（手机号或邮箱）
 * - accountType: 账号类型（3=手机, 4=邮箱）
 * - areaNumber: 区号（手机登录时）
 * - pwd: 密码
 *
 * @returns 加密后的请求体
 */
export async function getEncryptedFormDataLogin(option: {
  account: string;
  accountType: number;
  areaNumber?: string;
  pwd: string;
}): Promise<{
  account: {
    user_id: string;
    user_type: number;
    area_id?: string;
  };
  auth_info: string;
  key_info: {
    client_pub?: string;
    key_version?: number;
  };
}> {
  const { account, accountType, areaNumber, pwd } = option;

  // 明文的账号信息
  const accountInfo: {
    user_id: string;
    user_type: number;
    area_id?: string;
  } = {
    user_id: account,
    user_type: accountType,
  };

  // 手机登录需要区号
  if (accountType === 3 && areaNumber) {
    accountInfo.area_id = areaNumber.startsWith('+') ? areaNumber : `+${areaNumber}`;
  }

  let authInfo: string;
  let eccKeyInfoObj: { clientPublicKeyHex: string; sharedSecretHex: string } | null = null;

  try {
    // 动态获取协商密钥以及客户端公钥
    eccKeyInfoObj = await createEcdh();
  } catch (e) {
    console.error('createEcdh failed:', e);
  }

  const videoAppid = getCookieAmend('video_appid') || '1200012';

  if (eccKeyInfoObj) {
    // 客户端支持 ECC 加解密
    const { sharedSecretHex } = eccKeyInfoObj;

    // 构建登录数据
    const data: {
      account: typeof accountInfo;
      auth_token: { login_token: string };
      device_info: { guid: string; vplatform: number };
      timestamp: number;
    } = {
      account: accountInfo,
      auth_token: {
        login_token: '',
      },
      device_info: {
        guid: (getCookieAmend('guid') || '').toUpperCase(),
        vplatform: 2,
      },
      timestamp: Math.floor(Date.now() / 1000),
    };

    // 根据平台处理密码
    switch (videoAppid) {
      case '1200012': // WeTV - 使用 SHA256 哈希
        {
          const sha256Hash = CryptoJS.SHA256(pwd).toString(CryptoJS.enc.Hex);
          data.auth_token.login_token = sha256Hash;
        }
        break;
      case '1200016': // iflix - 明文密码
        data.auth_token.login_token = pwd || '';
        break;
      default:
        {
          const sha256Hash = CryptoJS.SHA256(pwd).toString(CryptoJS.enc.Hex);
          data.auth_token.login_token = sha256Hash;
        }
        break;
    }

    // AES-GCM 加密
    const encrypted = await aESGCM(JSON.stringify(data), sharedSecretHex);
    authInfo = arrayBufferToHex(encrypted.buffer as ArrayBuffer);
  } else {
    // 客户端不支持 ECC 加解密，降级方案
    switch (videoAppid) {
      case '1200012': // WeTV
        authInfo = CryptoJS.SHA256(pwd).toString(CryptoJS.enc.Hex);
        break;
      case '1200016': // iflix
        authInfo = pwd;
        break;
      default:
        authInfo = CryptoJS.SHA256(pwd).toString(CryptoJS.enc.Hex);
        break;
    }
  }

  // 构建请求体
  const reqOut: {
    account: typeof accountInfo;
    auth_info: string;
    key_info: {
      client_pub?: string;
      key_version?: number;
    };
  } = {
    account: accountInfo,
    auth_info: authInfo,
    key_info: {},
  };

  // 如果存在协商密钥对象，添加客户端公钥信息
  if (eccKeyInfoObj?.clientPublicKeyHex) {
    reqOut.key_info = {
      client_pub: eccKeyInfoObj.clientPublicKeyHex,
      key_version: 1,
    };
  }

  return reqOut;
}

// 导出其他可能需要的工具函数
export { aESGCM, hexToArrayBuffer, arrayBufferToHex, createEcdh, CryptoJS, Base64 };
